/**
 * submitSurveyAnswer: アンケートの回答を送信し、次のアクションを判定する。
 * US-D2-02/06、FR-Y2, FR-C9。BR-U6-2: 呼び出し前にログイン状態を検証する。
 */

import { relayClient } from "../core/relayClient";
import { classifyError, logClientWarning } from "../core/errorHandler";
import { isLoggedIn } from "../core/authState";
import type { SurveyChoiceAction } from "../types";

export interface SubmitSurveyAnswerParams {
  surveyId: string;
  scenarioId: string;
  objectType: string;
  objectRootId: string;
  selectedChoiceId: string;
  freeText?: string;
}

export interface SubmitSurveyAnswerResult {
  success: boolean;
  requiresLogin: boolean;
  action?: SurveyChoiceAction;
  linkUrl?: string | null;
  linkDescription?: string | null;
  /**
   * アンケート回答ログID（survey_response_log.id）。回答成功時のみ設定。
   * アンケート回答経由でアポイント導線へ進む場合に引き継ぎ、アポイント作成時に明示送信する
   * （2026-09-24 BR-U2-21 誤紐付け修正）。
   */
  surveyResponseLogId?: string;
}

interface RecordSurveyResponseData {
  id: string;
  selectedChoiceAction: SurveyChoiceAction;
  linkUrl: string | null;
  linkDescription: string | null;
}

export async function submitSurveyAnswer(
  params: SubmitSurveyAnswerParams
): Promise<SubmitSurveyAnswerResult> {
  if (!isLoggedIn()) {
    // US-D2-01 Scenario: 回答しようとした時点でログイン誘導（呼び出し元でpromptVisitorLoginを実行）
    return { success: false, requiresLogin: true };
  }

  const response = await relayClient.recordSurveyResponse<RecordSurveyResponseData>({
    surveyId: params.surveyId,
    scenarioId: params.scenarioId,
    objectType: params.objectType,
    objectRootId: params.objectRootId,
    selectedChoiceId: params.selectedChoiceId,
    freeText: params.freeText,
  });

  if (response.status === 401) {
    // トークン期限切れ等
    return { success: false, requiresLogin: true };
  }

  if (!response.ok || !response.data) {
    const classified = classifyError(response);
    logClientWarning(`submitSurveyAnswer failed: ${classified.kind}`);
    return { success: false, requiresLogin: false };
  }

  return {
    success: true,
    requiresLogin: false,
    action: response.data.selectedChoiceAction,
    linkUrl: response.data.linkUrl,
    linkDescription: response.data.linkDescription,
    surveyResponseLogId: response.data.id,
  };
}
